/**
 * AgentStatCard (spike) — the derived four-axis "character sheet" for one agent:
 * capability/intelligence, execution power, personality, language skills. Monochrome,
 * dot-meter styling (branding: no rainbow). Presentational only; promoted to the real
 * AgentDetailPanel later with the same shape.
 */
import React from 'react';
import { Globe, Palette, HeartHandshake, Bot, type LucideIcon } from 'lucide-react';
import type { AgentStats, Score } from './sampleData';

const ICONS: Record<string, LucideIcon> = { Globe, Palette, HeartHandshake };

/** A 0-5 dot meter — filled dots near-black/white, empty dots muted. */
const DotMeter: React.FC<{ value: Score }> = ({ value }) => (
  <span className="inline-flex items-center gap-1" aria-label={`${value} of 5`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <span
        key={i}
        className={`w-1.5 h-1.5 rounded-full ${i <= value ? 'bg-secondary-900 dark:bg-white' : 'bg-secondary-200 dark:bg-secondary-700'}`}
      />
    ))}
  </span>
);

const StatRow: React.FC<{ label: string; value: Score }> = ({ label, value }) => (
  <div className="flex items-center justify-between">
    <span className="text-xs text-secondary-500 dark:text-secondary-300">{label}</span>
    <DotMeter value={value} />
  </div>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div>
    <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-secondary-400">{title}</p>
    <div className="space-y-1.5">{children}</div>
  </div>
);

const Chip: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="inline-flex items-center rounded-full border border-secondary-200 dark:border-secondary-700 px-2 py-0.5 text-[11px] text-secondary-600 dark:text-secondary-200">
    {children}
  </span>
);

interface Props { name: string; nativeName?: string; persona: string; icon: string; stats: AgentStats }

const AgentStatCard: React.FC<Props> = ({ name, nativeName, persona, icon, stats }) => {
  const Icon = ICONS[icon] || Bot;
  const p = stats.personality;
  return (
    <div className="rounded-2xl border border-secondary-200 dark:border-secondary-800 bg-white dark:bg-secondary-900 p-5 space-y-4">
      {/* header */}
      <div className="flex items-center gap-3">
        <span className="flex items-center justify-center w-11 h-11 rounded-full bg-secondary-100 dark:bg-secondary-800 text-secondary-900 dark:text-white">
          <Icon className="w-5 h-5" />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-secondary-900 dark:text-white truncate">
            {name}{nativeName && <span className="font-normal text-secondary-400"> · {nativeName}</span>}
          </p>
          <p className="text-xs text-secondary-400 truncate">{persona}</p>
        </div>
      </div>

      {/* capability / intelligence */}
      <Section title="Capability">
        <StatRow label="Intelligence" value={stats.intelligence} />
        <StatRow label="Knowledge" value={stats.knowledge} />
      </Section>

      {/* execution power */}
      <Section title="Execution power">
        <StatRow label="Execution" value={stats.execution} />
        <div className="flex flex-wrap gap-1.5 pt-1">
          {stats.powers.map((pw) => <Chip key={pw}>{pw}</Chip>)}
        </div>
      </Section>

      {/* personality */}
      <Section title="Personality">
        <div className="flex flex-wrap gap-1.5 pb-1">
          <Chip>{p.archetype}</Chip>
          <Chip>{p.mbti}</Chip>
          <Chip>{p.humor} humor</Chip>
        </div>
        <StatRow label="Warmth" value={p.warmth} />
        <StatRow label="Energy" value={p.energy} />
        <StatRow label="Wit" value={p.wit} />
        <StatRow label="Creativity" value={p.creativity} />
      </Section>

      {/* language skills */}
      <Section title="Language skills">
        <div className="flex flex-wrap gap-1.5">
          {stats.languages.map((l) => <Chip key={l.name}>{l.name} · {l.fluency}</Chip>)}
        </div>
      </Section>
    </div>
  );
};

export default AgentStatCard;
